import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import ReactDOM from 'react-dom';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import { ArrowRight, CalendarSearchIcon, Undo2 } from 'lucide-react';
import moment from 'moment';
import { DayPickerRangeController } from 'react-dates';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';

import type {
  FilterConfig,
  TableFilterProps
} from '@/components/ui/table/filters/filters.type';

const FILTER_PLACEHOLDER = 'Select a date range';
const START_DATE_INPUT_PLACEHOLDER = 'Start Date';
const END_DATE_INPUT_PLACEHOLDER = 'End Date';

const START_DATE_FOCUS = 'startDate';
const END_DATE_FOCUS = 'endDate';
const MINIMUM_VALID_DATE_INPUT = moment.utc('01-01-1000', 'DD-MM-YYYY', true);

const isValidDateInput = (dateInput: Maybe<moment.Moment>) =>
  !dateInput || (dateInput.isValid() && dateInput >= MINIMUM_VALID_DATE_INPUT);

const voidFunction = () => {};

export type DateFilterValue = {
  startDate?: string | undefined;
  endDate?: string | undefined;
};

export const DateRangeFilter = React.memo(
  ({
    colConfig,
    setColumnFilter,
    filterConfig
  }: TableFilterProps<'dateRange'>) => {
    // check the filter value from column config
    let filterValue: FilterConfig<'dateRange'>['value'] = {};
    let initialFocus = START_DATE_FOCUS;
    if (colConfig.filter) {
      // check if the filter value type is correct (eg if the config changed the filter value type may change...)
      const filterConfigValue = colConfig.filter.value;
      if (
        !!filterConfigValue &&
        typeof filterConfigValue === 'object' &&
        !Array.isArray(filterConfigValue)
      ) {
        filterValue = filterConfigValue;
        // if start date is set initially and end date is not, focus end date immediately
        if (!!filterValue.startDate && !filterValue.endDate) {
          initialFocus = END_DATE_FOCUS;
        }
      }
    }

    // show date range picker
    // const [showPicker, , closePicker, btnRef] = useFocus(false, true);

    // start and end dates are strings
    const [showPicker, setShowPicker] = useState(false);
    const [{ startDate, endDate }, setDates] =
      useState<DateFilterValue>(filterValue);

    // start and end dates input strings, typed manually, before being validated as real dates
    const startDateInputRef = useRef<HTMLInputElement | null>(null);
    const endDateInputRef = useRef<HTMLInputElement | null>(null);
    const [
      { startDate: startDateInputStr, endDate: endDateInputStr },
      setDatesInputStr
    ] = useState<DateFilterValue>(filterValue);

    // state for which date the picker should focus on
    const [datePickerFocus, setDatePickerFocus] =
      useState<string>(initialFocus);

    // Update state when filter value changes by save view
    useEffect(() => {
      const { startDate: newStartDate, endDate: newEndDate } =
        colConfig.filter?.value || {};
      setDates({ startDate: newStartDate, endDate: newEndDate });
      setDatesInputStr({ startDate: newStartDate, endDate: newEndDate });
    }, [colConfig.filter]);

    // handle inputs refs and set date focus (both input and picker)
    const setDateFocus = useCallback(focus => {
      setDatePickerFocus(focus);
      if (focus === START_DATE_FOCUS) {
        if (startDateInputRef.current) startDateInputRef.current.focus();
      } else if (endDateInputRef.current) endDateInputRef.current.focus();
    }, []);

    // receive new set of dates object (from date picker or from parsed string inputs)
    const onDatesChange = useCallback(
      (
        newDates: Partial<{
          startDate: Maybe<moment.Moment>;
          endDate: Maybe<moment.Moment>;
        }>
      ) => {
        ReactDOM.unstable_batchedUpdates(() => {
          // read the new dates
          const { startDate: newStartDate, endDate: newEndDate } = newDates;

          // cast them to string
          const newStartDateStr = newStartDate
            ? newStartDate.format(filterConfig.format)
            : undefined;
          const newEndDateStr = newEndDate
            ? newEndDate.format(filterConfig.format)
            : undefined;

          // update component display state
          const newDatesStr = {
            startDate: newStartDateStr,
            endDate: newEndDateStr
          };
          setDates(newDatesStr);
          setDatesInputStr(newDatesStr);

          // what to focus next ? this depends on the new setup
          switch (true) {
            case !!newStartDateStr && !!newEndDateStr:
              // both dates are set and we can close the tooltip
              // keeping the current focus
              setShowPicker(false);
              break;
            case !!newStartDateStr:
              // start date is set but not end date, focus end date
              setDateFocus(END_DATE_FOCUS);
              break;
            case !!newEndDateStr:
              // end date is set but not start date, focus start date
              setDateFocus(START_DATE_FOCUS);
              break;
            default:
            // no date set which means that we've just deleted one of them
            // or both via 'reset dates'
            // keep the current focus
          }

          // reset filter setup
          setColumnFilter(
            colConfig.key,
            newStartDateStr || newEndDateStr
              ? {
                  startDate: newStartDateStr,
                  endDate: newEndDateStr
                }
              : undefined,
            filterConfig
          );
        });
      },
      [filterConfig, colConfig.key, setColumnFilter, setDateFocus]
    );

    // build a memo to get start and end date objects
    const { startDateObj, endDateObj } = useMemo(
      () => ({
        startDateObj: startDate
          ? moment.utc(startDate, filterConfig.format, true)
          : undefined,
        endDateObj: endDate
          ? moment.utc(endDate, filterConfig.format, true)
          : undefined
      }),
      [startDate, endDate, filterConfig]
    );

    // handling start and end dates direct input fields
    const onStartDateInputStrChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // read the new input string and convert it to a date
        const newStartDateStr = e.target.value;
        const newStartDateObj = newStartDateStr
          ? moment.utc(newStartDateStr, filterConfig.format, true)
          : undefined;

        // only update main dates if this is a valid date
        if (isValidDateInput(newStartDateObj)) {
          onDatesChange({
            startDate: newStartDateObj,
            endDate: endDateObj
          });
        } else {
          // otherwise update only dates inputs strings
          setDatesInputStr({
            startDate: newStartDateStr,
            endDate
          });
        }
      },
      [endDate, endDateObj, filterConfig, onDatesChange]
    );

    const onEndDateInputStrChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // read the new input string and convert it to a date
        const newEndDateStr = e.target.value;
        const newEndDateObj = newEndDateStr
          ? moment.utc(newEndDateStr, filterConfig.format, true)
          : undefined;

        // only update main dates if this is a valid date
        if (isValidDateInput(newEndDateObj)) {
          onDatesChange({
            startDate: startDateObj,
            endDate: newEndDateObj
          });
        } else {
          // otherwise update only dates inputs strings
          setDatesInputStr({
            startDate,
            endDate: newEndDateStr
          });
        }
      },
      [startDate, startDateObj, filterConfig, onDatesChange]
    );

    // This renders the Calendar section where we can clear the dates
    const renderCalendarInfo = useCallback(
      () => (
        <Button
          className="font-normal text-muted-foreground normal-case"
          data-cy={`reset-dates-button-${colConfig.key}`}
          onClick={() => {
            onDatesChange({});
          }}
          variant="ghost"
        >
          <Undo2 size={14} />
          <span className="text-muted-foreground text-xs">Reset dates</span>
        </Button>
      ),
      [onDatesChange, colConfig.key]
    );

    const buttonLabel = useMemo(() => {
      if (!!startDate && !!endDate) {
        if (startDate === endDate) {
          return startDate;
        }
        return `${startDate} - ${endDate}`;
      }
      if (startDate) {
        return `>= ${startDate}`;
      }
      if (endDate) {
        return `<= ${endDate}`;
      }
      return filterConfig.placeholder || FILTER_PLACEHOLDER;
    }, [filterConfig, startDate, endDate]);

    return (
      <Popover onOpenChange={setShowPicker} open={showPicker}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              'flex h-9 w-full flex-row flex-nowrap items-center justify-between overflow-hidden rounded-md border border-input bg-card px-3 font-normal text-muted-foreground text-sm normal-case tracking-normal hover:bg-inherit active:scale-100 disabled:pointer-events-auto disabled:cursor-auto disabled:opacity-70 [&_svg]:pointer-events-auto',
              (startDate || endDate) && 'text-card-foreground'
            )}
            data-cy={`filter-button-${colConfig.key}`}
            onClick={() => setShowPicker(p => !p)}
            type="button"
          >
            <span className="truncate" title={buttonLabel}>
              {buttonLabel}
            </span>
            {!(startDate || endDate) && (
              <CalendarSearchIcon className="mr-1 ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-fit max-w-none" side="bottom">
          <div className="flex flex-col items-stretch bg-card">
            <div className="relative my-0 flex h-11 w-full flex-row flex-nowrap items-center justify-between">
              <input
                className={cn(
                  'h-full grow border-border border-b-2 px-4 text-foreground text-sm outline-none transition-colors placeholder:text-muted-foreground hover:border-input',
                  datePickerFocus === START_DATE_FOCUS &&
                    'border-primary hover:border-primary'
                )}
                data-cy={`start-date-input-${colConfig.key}`}
                onChange={onStartDateInputStrChange}
                onFocus={() => setDatePickerFocus(START_DATE_FOCUS)}
                placeholder={START_DATE_INPUT_PLACEHOLDER}
                ref={startDateInputRef}
                type="search"
                value={startDateInputStr || ''}
              />
              <div className="flex h-full shrink-0 items-center justify-center border-b-2 px-4 text-foreground">
                <ArrowRight size={16} />
              </div>
              <input
                className={cn(
                  'h-full grow border-border border-b-2 px-4 text-foreground text-sm outline-none transition-colors placeholder:text-muted-foreground hover:border-input',
                  datePickerFocus === END_DATE_FOCUS &&
                    'border-primary hover:border-primary'
                )}
                data-cy={`end-date-input-${colConfig.key}`}
                onChange={onEndDateInputStrChange}
                onFocus={() => setDatePickerFocus(END_DATE_FOCUS)}
                placeholder={END_DATE_INPUT_PLACEHOLDER}
                ref={endDateInputRef}
                type="search"
                value={endDateInputStr || ''}
              />
            </div>
            <div className="grow">
              <DayPickerRangeController
                enableOutsideDays
                endDate={endDateObj}
                focusedInput={datePickerFocus}
                hideKeyboardShortcutsPanel
                initialVisibleMonth={null} // component re-focus after date selection is handled in onDatesChange
                minimumNights={0}
                noBorder
                numberOfMonths={2}
                onDatesChange={onDatesChange}
                onFocusChange={voidFunction}
                renderCalendarInfo={renderCalendarInfo}
                startDate={startDateObj}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
